/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/**
 * The display size is the rotated camera picture. It differs from the canvas
 * after a camera switch. An empty frame must not count: there is nothing to
 * draw yet. The caller rebuilds the drawing surface once for that size.
 */
/**
 * LiveKit leaves the first camera frame untouched and waits for a hidden
 * video element to paint. On iOS that wait does not finish until the
 * processor is created again, so the still image stays hidden while the
 * setting is already on. A loaded still image is drawn on the first frame.
 */
export function deferBackgroundUntilNextFrame(hasStillImage: boolean): boolean {
  return !hasStillImage;
}

/**
 * Where to draw an image so it fills `target` and the overflow is cut off.
 * A wide picture on a tall camera must lose its sides, not be squeezed.
 */
export function coverDrawRect(
  imageWidth: number,
  imageHeight: number,
  targetWidth: number,
  targetHeight: number,
): { dx: number; dy: number; dw: number; dh: number } {
  const scale = Math.max(targetWidth / imageWidth, targetHeight / imageHeight);
  const dw = imageWidth * scale;
  const dh = imageHeight * scale;
  return {
    dx: (targetWidth - dw) / 2,
    dy: (targetHeight - dh) / 2,
    dw,
    dh,
  };
}

/**
 * The fallback canvas keeps the size it had at startup. Copying a wider
 * frame into that rectangle squeezes it. Use the frame's own size instead.
 */
export function frameDrawSize(
  frameWidth: number,
  frameHeight: number,
  canvasWidth: number,
  canvasHeight: number,
): { width: number; height: number; resize: boolean } {
  if (frameWidth <= 0 || frameHeight <= 0) {
    return { width: canvasWidth, height: canvasHeight, resize: false };
  }
  return {
    width: frameWidth,
    height: frameHeight,
    resize: canvasWidth !== frameWidth || canvasHeight !== frameHeight,
  };
}

export function backgroundRenderingIsStale(
  canvasWidth: number,
  canvasHeight: number,
  frameWidth: number,
  frameHeight: number,
  contextLost: boolean,
): boolean {
  if (frameWidth <= 0 || frameHeight <= 0) return false;
  if (contextLost) return true;
  return canvasWidth !== frameWidth || canvasHeight !== frameHeight;
}

type SizedCanvas = {
  width: number;
  height: number;
};

type DimensionDescriptor = PropertyDescriptor & {
  get: () => number;
  set: (value: number) => void;
};

function dimensionDescriptor(
  canvas: SizedCanvas,
  name: "width" | "height",
): DimensionDescriptor | undefined {
  const prototype: object | null = Object.getPrototypeOf(canvas);
  if (prototype === null) return undefined;
  const descriptor = Object.getOwnPropertyDescriptor(prototype, name);
  if (descriptor?.get === undefined || descriptor.set === undefined) {
    return undefined;
  }
  return descriptor as DimensionDescriptor;
}

/**
 * LiveKit writes the frame size onto the canvas on every frame. On WebKit
 * that write clears the bitmap and the WebGL context, even when the size
 * did not change. Ignore the write for the duration of `run`. A real size
 * change is not applied here: resizing this canvas drops the GPU segmenter.
 */
export async function whileCanvasSizeStays<T>(
  canvas: SizedCanvas,
  run: () => T | Promise<T>,
): Promise<T> {
  const widthDesc = dimensionDescriptor(canvas, "width");
  const heightDesc = dimensionDescriptor(canvas, "height");
  if (widthDesc === undefined || heightDesc === undefined) return await run();

  const guard = (descriptor: DimensionDescriptor): PropertyDescriptor => ({
    configurable: true,
    get: () => descriptor.get.call(canvas),
    set: (value: number) => {
      // See whileCanvasSizeStays. The value is the size LiveKit wanted.
      void value;
    },
  });

  try {
    Object.defineProperty(canvas, "width", guard(widthDesc));
    Object.defineProperty(canvas, "height", guard(heightDesc));
  } catch {
    Reflect.deleteProperty(canvas, "width");
    Reflect.deleteProperty(canvas, "height");
    return await run();
  }

  try {
    return await run();
  } finally {
    Reflect.deleteProperty(canvas, "width");
    Reflect.deleteProperty(canvas, "height");
  }
}
